import * as React from 'react';
// @ts-expect-error
import {Controller} from 'react-hook-form';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';

import {getHumanReadableEntityTypeFromGraphQLEnum} from 'nutshell-core/utils';
import {getTypeFromApiId} from 'nutshell-core/utils/get-type-from-api-id';

import {Button} from 'shells/button/button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {Body} from 'shells/typography/body';

import type {EntityType} from 'nutshell-graphql-types';
import type {FormFragment_editableVersion_fields_rules as FieldAnswerRule} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../../../analytics-client';
import type {FormSchemaProperty} from '../../../../../core/nutshell-fields/types';
import type {InputFormField} from '../../../../types';

import {PreselectAnswers} from '../preselect-answers';

import {AdditionalFormRule} from './additional-form-rule';
import {AnswerRuleCard} from './answer-rule-card';

type Props = {
    formId: string;
    fieldSchema: FormSchemaProperty;
    currentField: InputFormField;
    entityType: EntityType;
    name: string;
    watch: Watch;
    control: Control;
};

export function AdditionalRules(props: Props) {
    const rules = props.watch(`${props.name}.rules`) ?? [];

    const [isAddingRule, setIsAddingRule] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    React.useEffect(() => {
        setIsAddingRule(false);
        setIsSaving(false);
    }, [props.currentField.id]);

    const handleRuleChange = React.useCallback((field, newRules) => {
        field.onChange(newRules);

        return new Promise((resolve) => {
            // @ts-expect-error
            resolve();
        });
    }, []);

    const isDecisionField =
        props.fieldSchema.displayType === 'dropdown' ||
        props.fieldSchema.displayType === 'radio' ||
        props.fieldSchema.displayType === 'checkboxes';

    return (
        <div className='flex flex-dir-col gap-8 pt-16'>
            <Body isBold={true} color='dark' size='small'>
                Additional rules
            </Body>
            <Controller
                control={props.control}
                name={`${props.name}.rules`}
                // @ts-expect-error
                render={({field}) => (
                    <Checkbox
                        label={`Update ${getHumanReadableEntityTypeFromGraphQLEnum(
                            props.entityType
                        ).toLowerCase()} based on the answer to this question`}
                        checked={(rules && rules.length > 0) || isAddingRule}
                        onChange={() => {
                            if (!rules || rules.length === 0) {
                                setIsAddingRule(!isAddingRule);
                            } else {
                                handleRuleChange(field, []);
                            }
                        }}
                    />
                )}
            />
            {rules &&
                // @ts-expect-error
                rules.map((rule) =>
                    // @ts-expect-error
                    rule.objectsToAssign.map((objectToAssign) => (
                        <Controller
                            key={`${rule.answerChoice.id}-${objectToAssign.id}`}
                            control={props.control}
                            name={`${props.name}.rules`}
                            // @ts-expect-error
                            render={({field}) => (
                                <AnswerRuleCard
                                    key={`${rule.answerChoice.id}-${objectToAssign.id}`}
                                    fieldLabel={props.currentField.label}
                                    selectedOption={rule.answerChoice.name || ''}
                                    objectToAssign={objectToAssign}
                                    onRemove={() => {
                                        handleRuleChange(
                                            field,
                                            removeRule(
                                                rule.answerChoice.id,
                                                objectToAssign.id,
                                                rules
                                            )
                                        );
                                    }}
                                />
                            )}
                        />
                    ))
                )}
            {!isAddingRule && rules && rules.length > 0 && (
                <Button variant='text-primary' onClick={() => setIsAddingRule(true)}>
                    + Add another rule
                </Button>
            )}
            {isAddingRule && (
                <Controller
                    control={props.control}
                    name={`${props.name}.rules`}
                    // @ts-expect-error
                    render={({field}) => (
                        <AdditionalFormRule
                            fieldSchema={props.fieldSchema}
                            currentField={props.currentField}
                            formId={props.formId}
                            onSave={(
                                answerEnumId: string,
                                assignmentId: string,
                                assignmentName: string
                            ) => {
                                setIsSaving(true);

                                const selectedOption = props.fieldSchema.options.find(
                                    (option) => option.id === answerEnumId
                                );

                                const newRules = addRule(
                                    answerEnumId,
                                    (selectedOption && selectedOption.name) || '',
                                    assignmentId,
                                    assignmentName,
                                    rules,
                                    props.entityType
                                );

                                handleRuleChange(field, newRules).then(() => {
                                    setIsSaving(false);
                                    setIsAddingRule(false);
                                });
                            }}
                            isSaving={isSaving}
                            onCancel={() => setIsAddingRule(false)}
                        />
                    )}
                />
            )}
            {isDecisionField && (
                <PreselectAnswers
                    name={props.name}
                    watch={props.watch}
                    control={props.control}
                    fieldType={props.fieldSchema.displayType}
                    answerOptions={props.fieldSchema.options}
                />
            )}
        </div>
    );
}

const addRule = (
    answerEnumId: string,
    answerEnumName: string,
    assignmentId: string,
    assignmentName: string,
    rules: FieldAnswerRule[],
    fieldEntityType: EntityType
) => {
    analyticsClient.track(analyticsClient.EVENTS.TRACK_FORM_ANSWER_RULE_CREATED, {
        fieldType: fieldEntityType.toLowerCase(),
        entityType: getTypeFromApiId(assignmentId),
    });

    const existingRule = rules.find((rule) => rule.answerChoice.id === answerEnumId);
    if (existingRule) {
        if (existingRule.objectsToAssign.some((object) => object.id === assignmentId)) {
            // attempting to create a duplicate rule
            return rules;
        } else {
            // adding a new assignment to an existing rule
            return rules.map((rule) =>
                rule.answerChoice.id === answerEnumId
                    ? {
                          ...rule,
                          objectsToAssign: [
                              ...rule.objectsToAssign,
                              {id: assignmentId, name: assignmentName},
                          ],
                      }
                    : rule
            );
        }
    } else {
        // creating a new rule
        return [
            ...rules,
            {
                answerChoice: {id: answerEnumId, name: answerEnumName},
                objectsToAssign: [{id: assignmentId, name: assignmentName}],
            },
        ];
    }
};

const removeRule = (answerEnumId: string, assignmentId: string, rules: FieldAnswerRule[]) => {
    if (
        rules.find(
            (rule) =>
                rule.answerChoice.id === answerEnumId &&
                rule.objectsToAssign.some((object) => object.id === assignmentId) &&
                rule.objectsToAssign.length > 1
        )
    ) {
        // remove one assignment from an existing rule
        return rules.map((rule) =>
            rule.answerChoice.id === answerEnumId &&
            rule.objectsToAssign.some((object) => object.id === assignmentId)
                ? {
                      ...rule,
                      objectsToAssign: rule.objectsToAssign.filter(
                          (object) => object.id !== assignmentId
                      ),
                  }
                : rule
        );
    } else {
        // remove the rule if there is only one assignment
        return rules.filter((rule) => rule.answerChoice.id !== answerEnumId);
    }
};
